import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Stack,
  IconButton,
  Switch,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider
} from '@mui/material';
import {
  Close as CloseIcon,
  Settings as SettingsIcon,
  Cookie as CookieIcon
} from '@mui/icons-material';
import {
  hasConsentChoice,
  acceptAllCookies,
  rejectAllCookies,
  saveConsentStatus,
  getConsentStatus
} from '../utils/consent';

const CookieConsentBanner = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [consent, setConsent] = useState({
    analytics: false,
    advertising: false,
    functional: false
  });

  useEffect(() => {
    // Show banner if user hasn't made a choice
    if (!hasConsentChoice()) {
      setShowBanner(true);
    }
  }, []);

  useEffect(() => {
    // Load current consent settings
    const currentConsent = getConsentStatus();
    if (currentConsent) {
      setConsent({
        analytics: currentConsent.analytics,
        advertising: currentConsent.advertising,
        functional: currentConsent.functional
      });
    }
  }, []);

  const handleAcceptAll = () => {
    acceptAllCookies();
    setShowBanner(false);
  };

  const handleRejectAll = () => {
    rejectAllCookies();
    setShowBanner(false);
  };

  const handleSaveSettings = () => {
    saveConsentStatus(consent);
    setShowBanner(false);
  };

  const handleConsentChange = (category) => (event) => {
    setConsent(prev => ({
      ...prev,
      [category]: event.target.checked
    }));
  };

  if (!showBanner) {
    return null;
  }

  return (
    <>
      {/* Main horizontal banner */}
      <Box
        sx={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 9999,
          backgroundColor: 'background.paper',
          borderTop: '1px solid',
          borderColor: 'divider',
          boxShadow: '0 -2px 10px rgba(0,0,0,0.1)'
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 3,
            py: 2,
            maxWidth: '100%',
            gap: 2
          }}
        >
          {/* Left side - Text content */}
          <Box sx={{ display: 'flex', alignItems: 'center', flex: 1, minWidth: 0 }}>
            <CookieIcon sx={{ mr: 1.5, color: 'primary.main', flexShrink: 0 }} />
            <Typography 
              variant="body2" 
              sx={{ 
                color: 'text.primary',
                fontSize: '0.875rem',
                lineHeight: 1.4
              }}
            >
              We use cookies to enhance your browsing experience, analyze site traffic, and provide personalized content. By clicking "ACCEPT", you consent to our use of cookies.
            </Typography>
          </Box>

          {/* Right side - Buttons */}
          <Stack 
            direction="row" 
            spacing={1} 
            sx={{ 
              flexShrink: 0,
              alignItems: 'center'
            }}
          >
            <Button
              variant="outlined"
              size="small"
              onClick={() => setShowSettings(true)}
              sx={{
                minWidth: 'auto',
                px: 2,
                py: 0.5,
                fontSize: '0.75rem',
                fontWeight: 500,
                textTransform: 'none',
                borderColor: 'grey.400',
                color: 'text.primary',
                '&:hover': {
                  borderColor: 'grey.600',
                  backgroundColor: 'grey.50'
                }
              }}
            >
              Cookie settings
            </Button>
            
            <Button
              variant="contained"
              size="small"
              onClick={handleAcceptAll}
              sx={{
                minWidth: 'auto',
                px: 2,
                py: 0.5,
                fontSize: '0.75rem',
                fontWeight: 600,
                textTransform: 'uppercase',
                backgroundColor: 'grey.800',
                color: 'white',
                '&:hover': {
                  backgroundColor: 'grey.900'
                }
              }}
            >
              ACCEPT
            </Button>

            <IconButton
              size="small"
              onClick={() => setShowBanner(false)}
              sx={{ 
                ml: 1,
                color: 'grey.600',
                '&:hover': {
                  backgroundColor: 'grey.100'
                }
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Stack>
        </Box>
      </Box>

      {/* Cookie Settings Modal */}
      <Dialog 
        open={showSettings} 
        onClose={() => setShowSettings(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)'
          }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          pb: 1
        }}>
          <Typography variant="h6" component="div">
            Cookie Preferences
          </Typography>
          <IconButton
            size="small"
            onClick={() => setShowSettings(false)}
            sx={{ color: 'grey.600' }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ pt: 2 }}>
          <Typography variant="body2" color="text.secondary" paragraph>
            Manage your cookie preferences. You can enable or disable different types 
            of cookies below. Note that disabling some cookies may affect your experience.
          </Typography>

          <Stack spacing={2.5}>
            <Box>
              <FormControlLabel
                control={<Switch checked={true} disabled />}
                label={
                  <Box>
                    <Typography variant="body2" fontWeight={500}>
                      Necessary Cookies
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Required for the website to function properly. These cannot be disabled.
                    </Typography>
                  </Box>
                }
              />
            </Box>

            <Divider />

            <Box>
              <FormControlLabel
                control={
                  <Switch
                    checked={consent.analytics}
                    onChange={handleConsentChange('analytics')}
                  />
                }
                label={
                  <Box>
                    <Typography variant="body2" fontWeight={500}>
                      Analytics Cookies
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Help us understand how visitors interact with our website by collecting information anonymously.
                    </Typography>
                  </Box>
                }
              />
            </Box>

            <Divider />

            <Box>
              <FormControlLabel
                control={
                  <Switch
                    checked={consent.functional}
                    onChange={handleConsentChange('functional')}
                  />
                }
                label={
                  <Box>
                    <Typography variant="body2" fontWeight={500}>
                      Functional Cookies
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Enable enhanced functionality and personalization, such as remembering your preferences.
                    </Typography>
                  </Box>
                }
              />
            </Box>

            <Divider />

            <Box>
              <FormControlLabel
                control={
                  <Switch
                    checked={consent.advertising}
                    onChange={handleConsentChange('advertising')}
                  />
                }
                label={
                  <Box>
                    <Typography variant="body2" fontWeight={500}>
                      Advertising Cookies
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Used to deliver advertisements that are relevant to you and your interests.
                    </Typography>
                  </Box>
                }
              />
            </Box>
          </Stack>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3, pt: 2 }}>
          <Stack direction="row" spacing={1} sx={{ width: '100%' }}>
            <Button
              variant="outlined"
              onClick={handleRejectAll}
              sx={{
                px: 2,
                py: 1,
                fontSize: '0.875rem',
                textTransform: 'none',
                borderColor: 'grey.400',
                color: 'text.primary'
              }}
            >
              Reject All
            </Button>
            
            <Button
              variant="outlined"
              onClick={() => {
                acceptAllCookies();
                setShowSettings(false);
                setShowBanner(false);
              }}
              sx={{
                px: 2,
                py: 1,
                fontSize: '0.875rem',
                textTransform: 'none',
                borderColor: 'success.main',
                color: 'success.main',
                '&:hover': {
                  backgroundColor: 'success.lighter',
                  borderColor: 'success.dark'
                }
              }}
            >
              Accept All
            </Button>
            
            <Button
              variant="contained"
              onClick={handleSaveSettings}
              sx={{
                px: 2,
                py: 1,
                fontSize: '0.875rem',
                fontWeight: 600,
                textTransform: 'uppercase',
                backgroundColor: 'grey.800',
                ml: 'auto !important',
                '&:hover': {
                  backgroundColor: 'grey.900'
                }
              }}
            >
              SAVE PREFERENCES
            </Button>
          </Stack>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CookieConsentBanner;